// =============================================================================
// quoteReports — client helpers for the quote report/email subsystem.
//
// Backend (ReportController, api/reports/quote/...):
//   GET  /api/reports/quote/{n}?variant=&format=pdf&download=true  → PDF (auth)
//   POST /api/reports/quote/{n}/email?variant=                     → resend to
//          the standard recipients (sales rep, distributor, customer contact)
//   POST /api/reports/quote/{n}/send  { toEmail, cc, variant }     → send to an
//          explicit recipient (+ CC) list
//
// Every call requires the Authorization: Bearer <token> header, so report
// PDFs can't be opened with a plain <a href> — we fetch the bytes with the
// header and trigger a client-side save/open instead.
// =============================================================================
import sessionStorage from "./sessionStore";

const API_URL = process.env.REACT_APP_API_URL;

// The three report variants, in the order the dropdowns should show them.
// `value` is the API `variant` / create-body `reportStyle`; `label` is the UI text.
export const REPORT_STYLES = [
  { value: "system",   label: "System" },
  { value: "lines",    label: "System + Lines" },
  { value: "discount", label: "System + Lines + Discount" },
];

export const DEFAULT_REPORT_STYLE = "system";

function bearer() {
  const h = {};
  const t = sessionStorage.getItem("authToken");
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

// Download (or, with { open: true }, open inline) a quote report PDF.
export async function downloadQuoteReport(quoteNum, variant, { open = false } = {}) {
  const qs = `variant=${encodeURIComponent(variant)}&format=pdf${open ? "" : "&download=true"}`;
  const r = await fetch(`${API_URL}/api/reports/quote/${quoteNum}?${qs}`, { headers: bearer() });
  if (!r.ok) throw new Error(`Report failed (HTTP ${r.status})`);
  const blob = await r.blob();
  const objectUrl = URL.createObjectURL(blob);
  if (open) {
    window.open(objectUrl, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } else {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = `Quote_${quoteNum}_${variant}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}

// Resend the quote email to the standard recipients (sales rep, distributor,
// customer contact) with the chosen report style.
export async function resendQuoteEmail(quoteNum, variant) {
  const r = await fetch(
    `${API_URL}/api/reports/quote/${quoteNum}/email?variant=${encodeURIComponent(variant)}`,
    { method: "POST", headers: bearer() }
  );
  const data = await r.json().catch(() => ({}));
  if (!r.ok || data.success === false) throw new Error(data.message || `Send failed (HTTP ${r.status})`);
  return data;
}

// Send the quote report to explicit recipient(s) — first address is the To,
// the rest become CC — with the chosen report style.
export async function sendQuoteReportTo(quoteNum, variant, recipients, subject = null) {
  const list = (recipients || []).map((e) => String(e).trim()).filter(Boolean);
  if (list.length === 0) throw new Error("At least one recipient is required.");
  const [toEmail, ...cc] = list;
  const r = await fetch(`${API_URL}/api/reports/quote/${quoteNum}/send`, {
    method: "POST",
    headers: { ...bearer(), "Content-Type": "application/json" },
    body: JSON.stringify({ toEmail, cc, variant, subject }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || data.success === false) throw new Error(data.message || `Send failed (HTTP ${r.status})`);
  return data;
}
